import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, TogglerBlocks, VerticalContainer, Wrapper } from './ReportTimestamps.styles';
import {
  ExternalSign,
  HistoricalSign,
  ManagerSign,
  MeanSign,
  VariationSign,
} from './TimestampSigns/TimestampSigns';
import ValueColorBlocks from './ValueColorBlocks/ValueColorBlocks';
import parseRelatedTopics from '../../../../../helpers/effectReports/parseRelatedTopics';
import { TogglerKeys, useReportContext } from '../../../../../hooks/useReport';
import { Analysis } from '../../../../../services/reports';
import Toggler from '../../../../Toggler/Toggler';

interface ReportTimestampsProps {
  handleToggleState: (name: TogglerKeys) => void;
  data: Analysis;
}

const ReportTimestamps: React.FC<ReportTimestampsProps> = ({ handleToggleState, data }) => {
  const { effectData } = useReportContext();
  const matchingTopics = parseRelatedTopics(data, effectData);
  const firstTopic = matchingTopics[0];
  const isHistoricalValue = firstTopic?.historical_value;
  const isExternalBenchmark = firstTopic?.external_benchmark;
  const isSuperManagerValue = firstTopic?.super_manager_value;
  const isOrganizationValue = firstTopic?.organisation_value;

  const { t } = useTranslation();

  if (!firstTopic) {
    return null;
  }

  return (
    <Wrapper>
      <ValueColorBlocks shape="square" direction="row" flattened={true} data={data} />
      <TogglerBlocks>
        <VerticalContainer
          hasSecondColumn={Boolean(isSuperManagerValue || isOrganizationValue || isExternalBenchmark)}
        >
          {isHistoricalValue && (
            <div>
              <HistoricalSign />
              <Text>{t('report.effect.timestamps.historicalResults')}</Text>
              <Toggler name="historical_result" handleToggleState={handleToggleState} />
            </div>
          )}

          <div>
            <VariationSign />
            <Text>{t('report.effect.timestamps.variationBetweenRespondents')}</Text>
            <Toggler name="variation_between_respondents" handleToggleState={handleToggleState} />
          </div>
        </VerticalContainer>

        <VerticalContainer>
          {isSuperManagerValue && (
            <div>
              <ManagerSign />
              <Text>{t('report.effect.timestamps.teamManager')}</Text>
              <Toggler name="team_manager" handleToggleState={handleToggleState} />
            </div>
          )}

          {isOrganizationValue && (
            <div>
              <MeanSign />
              <Text>{t('report.effect.timestamps.organizationMean')}</Text>
              <Toggler name="organization_mean" handleToggleState={handleToggleState} />
            </div>
          )}

          {isExternalBenchmark && (
            <div>
              <ExternalSign />
              <Text>{t('report.effect.timestamps.externalBenchmark')}</Text>
              <Toggler name="external_benchmark" handleToggleState={handleToggleState} />
            </div>
          )}
        </VerticalContainer>
      </TogglerBlocks>
    </Wrapper>
  );
};

export default ReportTimestamps;
